import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { seasonPackCopy as copy } from '../data/seasonPackCopy';
import { actionPackMeta, actionPackBlocks } from '../data/seasonActionPack';
import { officialResources } from '../data/officialResources';
import { assembleContactEmail, ObfuscatedEmail } from '../components/ObfuscatedEmail';
import { AnalyticsEvents, trackEvent } from '../utils/analytics';
import { usePageMeta } from '../hooks/usePageMeta';
import { submitInquiry } from '../utils/submitInquiry';
import '../styles/Consulting.css';
import '../styles/SeasonPass.css';

const resourceById = Object.fromEntries(officialResources.map((r) => [r.id, r]));

const SeasonPass = () => {
  usePageMeta({
    title: '2026 입시 시즌패스',
    description:
      '연 1회 39,000원. 시즌 액션팩·패스 알림·Q&A 1회. PayPal 또는 계좌는 신청 후 메일 안내.',
    path: '/pass',
  });
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    track: 'eju',
    payment: 'either',
    seasons: { eju: true, mext: true, kyotsu: false },
    memo: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setCopied(false);
  };

  const handleSeason = (key) => (e) => {
    setFormData((prev) => ({
      ...prev,
      seasons: { ...prev.seasons, [key]: e.target.checked },
    }));
    setCopied(false);
  };

  const seasonList = useMemo(
    () =>
      [
        formData.seasons.eju && 'EJU',
        formData.seasons.mext && '국비(MEXT)',
        formData.seasons.kyotsu && '공통테스트',
      ].filter(Boolean),
    [formData.seasons]
  );

  const buildMailBody = useMemo(() => {
    const trackLabel = { eju: 'EJU 유학생', general: '일반입시', mext: '국비(MEXT)' }[
      formData.track
    ];
    const payLabel = {
      paypal: 'PayPal',
      bank: '계좌 이체',
      either: 'PayPal 또는 계좌 이체 (운영 안내)',
    }[formData.payment];
    return [
      '[미라이쥬쿠 시즌패스 신청]',
      `이름: ${formData.name || '—'}`,
      `이메일: ${formData.email || '—'}`,
      `목표 루트: ${trackLabel}`,
      `관심 시즌: ${seasonList.join(', ') || '없음'}`,
      `결제 희망: ${payLabel}`,
      '',
      '메모:',
      formData.memo || '-',
      '',
      '상품: 연 1회 · 39,000원',
    ].join('\n');
  }, [formData, seasonList]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildMailBody);
      setCopied(true);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('복사에 실패했습니다. 아래 미리보기에서 직접 선택해 복사해 주세요.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) return;
    if (!seasonList.length) {
      setError('관심 시즌을 하나 이상 선택해 주세요.');
      return;
    }

    setIsSubmitting(true);
    setSuccess(null);
    setError(null);

    const subject = `[미라이쥬쿠 시즌패스] ${formData.name}`;

    try {
      await submitInquiry({
        name: formData.name,
        email: formData.email,
        subject,
        message: buildMailBody,
        honey: formData.website,
        extra: { kind: 'season_pass', payment: formData.payment },
      });

      setSuccess(
        '신청이 전달되었습니다. 2~3영업일 내 PayPal 또는 계좌 이체 안내 메일을 보냅니다.'
      );
      trackEvent(AnalyticsEvents.PASS_SUBMIT, { payment: formData.payment });
    } catch (err) {
      console.error(err);
      setError('자동 전송에 실패했습니다. 아래 완성 문구를 복사해 메일로 직접 보내 주세요.');
      try {
        await navigator.clipboard.writeText(buildMailBody);
        setCopied(true);
      } catch (_) {
        /* preview still visible */
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBlockLinks = (block) => {
    const items = [];
    (block.resourceIds || []).forEach((id) => {
      const r = resourceById[id];
      if (r) items.push({ key: id, label: r.name, url: r.url });
    });
    (block.links || []).forEach((l, i) => {
      items.push({ key: `l-${i}`, label: l.label, url: l.url });
    });
    (block.paths || []).forEach((p, i) => {
      items.push({ key: `p-${i}`, label: p.label, path: p.path });
    });
    return items;
  };

  return (
    <div className="apple-page apple-season-pass">
      <header className="apple-page-hero">
        <p className="apple-eyebrow">{copy.eyebrow}</p>
        <h1 className="apple-display">{copy.title}</h1>
        <p className="apple-lede">{copy.lede}</p>
      </header>

      <section className="apple-consult-price" aria-label="가격">
        <p className="apple-consult-price-main">{copy.priceLabel}</p>
        <p className="apple-consult-price-note">{copy.priceNote}</p>
      </section>

      <div className="apple-consult-grid">
        <section>
          <h2>포함</h2>
          <ul>
            {copy.includes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <h2>미포함</h2>
          <ul>
            {copy.excludes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <h2>준비하면 좋은 것</h2>
          <ul>
            {copy.prepare.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <h2>진행</h2>
          <ol>
            {copy.process.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </section>
        <section>
          <h2>취소·환불</h2>
          <ul>
            {copy.policy.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="apple-consult-policy-link">
            <Link to="/refund">환불·결제 정책 전문</Link>
          </p>
        </section>
        <section>
          <h2>무료와의 차이</h2>
          <ul>
            <li>
              무료 <Link to="/alerts">시즌 알림</Link>은 시즌이 온다는 리마인더입니다.
            </li>
            <li>시즌패스는 회차·공고 확인 항목을 묶은 액션팩과 패스 전용 태그 알림입니다.</li>
            <li>
              1:1 루트 진단이 필요하면 <Link to="/consulting">90분 컨설팅</Link>을 이용하세요.
            </li>
          </ul>
        </section>
      </div>

      <section className="apple-consult-form-wrap">
        <h2>신청</h2>
        <p className="apple-consult-form-intro">{copy.formIntro}</p>

        {success && <div className="apple-consult-banner is-ok">{success}</div>}
        {error && <div className="apple-consult-banner is-err">{error}</div>}

        <form className="apple-consult-form" onSubmit={handleSubmit}>
          <div className="apple-consult-honeypot" aria-hidden="true">
            <label>
              website
              <input
                tabIndex={-1}
                autoComplete="off"
                name="website"
                value={formData.website}
                onChange={handleChange}
              />
            </label>
          </div>

          <div className="apple-consult-row">
            <label>
              이름 *
              <input name="name" value={formData.name} onChange={handleChange} required />
            </label>
            <label>
              이메일 *
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </label>
          </div>

          <label>
            목표 루트 *
            <select name="track" value={formData.track} onChange={handleChange}>
              <option value="eju">EJU 유학생</option>
              <option value="general">일반입시</option>
              <option value="mext">국비(MEXT)</option>
            </select>
          </label>

          <label>
            결제 희망 *
            <select name="payment" value={formData.payment} onChange={handleChange}>
              <option value="either">상관없음 (PayPal 또는 계좌)</option>
              <option value="paypal">PayPal</option>
              <option value="bank">계좌 이체</option>
            </select>
          </label>

          <fieldset className="apple-pass-seasons">
            <legend>관심 시즌 *</legend>
            <label className="apple-pass-check">
              <input
                type="checkbox"
                checked={formData.seasons.eju}
                onChange={handleSeason('eju')}
              />
              EJU
            </label>
            <label className="apple-pass-check">
              <input
                type="checkbox"
                checked={formData.seasons.mext}
                onChange={handleSeason('mext')}
              />
              국비(MEXT)
            </label>
            <label className="apple-pass-check">
              <input
                type="checkbox"
                checked={formData.seasons.kyotsu}
                onChange={handleSeason('kyotsu')}
              />
              공통테스트
            </label>
          </fieldset>

          <label>
            메모
            <textarea
              name="memo"
              value={formData.memo}
              onChange={handleChange}
              rows={3}
              placeholder="관심 대학·학부, 궁금한 점 (선택)"
            />
          </label>

          <div className="apple-consult-actions">
            <button type="submit" className="apple-btn apple-btn-primary" disabled={isSubmitting}>
              {isSubmitting ? '전송 중…' : '신청 보내기'}
            </button>
            <button type="button" className="apple-btn apple-btn-secondary" onClick={handleCopy}>
              {copied ? '복사됨' : '문구 복사'}
            </button>
          </div>
        </form>

        <div className="apple-consult-preview">
          <div className="apple-consult-preview-head">
            <h3>완성 문구 미리보기</h3>
            <p>자동 전송이 안 될 때, 복사해서 메일로 붙여 넣으면 됩니다.</p>
          </div>
          <pre>{buildMailBody}</pre>
        </div>
      </section>

      <section className="apple-pass-pack" id="action-pack" aria-label="액션팩 미리보기">
        <div className="apple-pass-pack-head">
          <p className="apple-eyebrow">Action Pack</p>
          <h2>{actionPackMeta.title}</h2>
          <p>{actionPackMeta.lede}</p>
          <p className="apple-pass-pack-note">{actionPackMeta.note}</p>
          <button
            type="button"
            className="apple-btn apple-btn-secondary"
            onClick={() => window.print()}
          >
            인쇄
          </button>
        </div>

        <div className="apple-pass-blocks">
          {actionPackBlocks.map((block) => {
            const links = renderBlockLinks(block);
            return (
              <article key={block.id} className="apple-pass-block">
                <p className="apple-pass-block-season">{block.season}</p>
                <h3>{block.title}</h3>
                <p className="apple-pass-block-focus">{block.focus}</p>
                <ul>
                  {block.checks.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                {links.length > 0 && (
                  <ul className="apple-pass-block-links">
                    {links.map((l) => (
                      <li key={l.key}>
                        {l.url ? (
                          <a href={l.url} target="_blank" rel="noopener noreferrer">
                            {l.label}
                          </a>
                        ) : (
                          <Link to={l.path}>{l.label}</Link>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <p className="apple-consult-foot-note">
        결제·패스 안내: <ObfuscatedEmail />
      </p>
      <p className="apple-consult-disclaimer">{copy.disclaimer}</p>

      <div className="apple-page-foot">
        <Link to="/alerts" className="apple-link">
          무료 시즌 알림
        </Link>
        <Link to="/consulting" className="apple-link">
          90분 컨설팅
        </Link>
        <Link to="/calendar" className="apple-link">
          시즌 일정
        </Link>
      </div>
    </div>
  );
};

export default SeasonPass;
